import { useMemo } from 'react';

import { maxBy, orderBy } from 'lodash';

import { useThemeStatusStore } from 'store/themeStatus.store';

import { SummaryCard } from 'api/queries/getProjectStatusScoreCards.query';

import { getColorByStatus } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/ProgramStatusSummaryReport/programStatusSummaryReport.utils';
import { WidgetCard } from 'components/common';
import classes from 'components/widgets/ProjectSummary/components/CardsSection/CardsSection.module.scss';
import { maxHeight, maxValueScale } from 'components/widgets/ProjectSummary/components/CardsSection/CardsSection.utils';
import { CardGraph } from 'components/widgets/ProjectSummary/components/CardsSection/components/CardGraph/CardGraph';
import {
    serializeData,
    serializeOptions,
} from 'components/widgets/ProjectSummary/components/CardsSection/components/RiskCard/RiskCard.utils';
import { RiskCardMetrics } from 'components/widgets/ProjectSummary/components/CardsSection/components/RiskCard/RiskCardMetrics';
import { NoApplicableData } from 'components/widgets/ProjectSummary/components/NoApplicableData/NoApplicableData';
import { ToggleCollapse } from 'components/widgets/ProjectSummary/components/ToggleCollapse/ToggleCollapse';

interface Props extends SummaryCard {
    collapse: boolean;
    setCollapse: (value: boolean) => void;
    chartHeight?: number | string;
    canExpand: boolean;
}

export const RiskCard = ({
    cardProperties,
    cardTitle,
    cardTrends,
    collapse,
    setCollapse,
    overallScore,
    chartHeight,
    canExpand,
}: Props) => {
    const sortedMetricsList = useMemo(() => orderBy(cardProperties.metrics, ['order', 'desc']), [cardProperties]);
    const showGraphSection = cardTrends?.[0];
    const height = !collapse && showGraphSection ? maxHeight : '100%';
    const maxValuePoint = maxBy(cardTrends?.[0]?.graphPoints, 'score');
    const maxValue = maxValuePoint ? maxValuePoint?.score * maxValueScale : undefined;
    const status = cardTitle.status;
    const tooltipContent =
        status === 'N/A' ? 'Not applicable with applied filter' : `Overall Score is ${overallScore.value}`;
    const id = `statusSummary_risk`;

    const { themeStatus } = useThemeStatusStore();

    return (
        <WidgetCard
            titleColor={getColorByStatus({ status, theme: themeStatus })}
            titleText={cardTitle.title}
            scoreValue={status}
            contentHeight={height}
            tooltipContent={tooltipContent}
            id={id}
        >
            {status === 'N/A' && <NoApplicableData id={id} />}
            {status !== 'N/A' && (
                <div className={classes.cardWrapper}>
                    {canExpand && (
                        <div className={classes.topSection}>
                            <div className={`${classes.metricsListContainer} ${collapse && classes.collapse}`}>
                                <RiskCardMetrics sortedMetricsList={sortedMetricsList} id={id} />
                            </div>
                            <ToggleCollapse setCollapse={setCollapse} collapse={collapse} id={id} />
                        </div>
                    )}
                    {showGraphSection && (
                        <CardGraph
                            data={cardTrends}
                            xaxisTitle={'Version'}
                            yaxisTitle={'Schedule Recovery Index'}
                            yAxisMaxValue={maxValue}
                            height={chartHeight}
                            serializeData={serializeData}
                            serializeOptions={serializeOptions}
                        />
                    )}
                </div>
            )}
        </WidgetCard>
    );
};
